/**
 * Route authorization, derived from the menu.
 *
 * Until now `/admin/*` had exactly one guard — `ISADMINLOGGED()` — so hiding a menu
 * item changed what a user saw and nothing about what they could reach. Every one of
 * the 75 routes was deep-linkable by any signed-in account.
 *
 * Policies are DERIVED from `menuData` rather than hand-listed beside it, so a menu
 * entry and its route cannot drift apart: the item's `roleActions` *is* the route's
 * policy. Only routes with no menu entry at all need a hand-written row, and
 * `route-policy.test.ts` fails if a new route appears without one.
 */

import { matchPath } from 'react-router';

import { pageurl } from '@/constants/pageurl';
import { menuData } from '@/layout/menu/components/data';
import { ACTIONS } from '@/layout/permissions/actions';
import type { IMenuItem } from '@/layout/utils';

import { hasAny, hasAnyRole, isSuperAdmin } from './authority';
import { isMenuItemVisible, subItemPath, visibleSubItems } from './menu-visibility';
import type { ActionSet, IPermissionSnapshot } from './types';

export interface IRoutePolicy {
	/** Absolute react-router path pattern. */
	path: string;
	/** OR semantics. `null` means "any authenticated user". */
	actions: ActionSet | null;
	superAdminOnly?: boolean;
	/** See `IMenuItem.requiredRoles` — any one of these roles, on top of `actions`. */
	requiredRoles?: readonly string[];
	/** See `IMenuItem.excludedRoles` — none of these roles, whatever the actions. */
	excludedRoles?: readonly string[];
	origin: 'menu-item' | 'menu-sub' | 'extra';
	/** For `extra` rows — why this policy, in one phrase. */
	note?: string;
}

/**
 * Routes with no menu entry of their own.
 *
 * Most detail routes need nothing here: prefix resolution gives
 * `/admin/vesr/incidents/:id` the Incidents policy for free. These are the genuine
 * orphans — pages reachable only from inside another screen, or redirects.
 */
const ROUTE_EXTRAS: IRoutePolicy[] = [
	{ path: pageurl.PROFILE, actions: null, origin: 'extra', note: 'Own profile — any signed-in user' },
	{ path: pageurl.SETTINGS, actions: null, origin: 'extra', note: 'Own settings — any signed-in user' },
	{ path: pageurl.ORGANIZATIONS, actions: [ACTIONS.ORGANIZATION], origin: 'extra' },
	{ path: pageurl.PRESIDINGOFFICER, actions: [ACTIONS.PRESIDING_OFFICERS], origin: 'extra' },

	// System Management pages that never got a sub-item.
	{ path: pageurl.NOTIFICATIONS, actions: [ACTIONS.SYSTEM_MANAGEMENT_NOTIFICATIONS, ACTIONS.SYSTEM_MANAGEMENT], origin: 'extra' },
	{ path: pageurl.REPORT, actions: [ACTIONS.SYSTEM_MANAGEMENT_FEEDBACK, ACTIONS.SYSTEM_MANAGEMENT], origin: 'extra' },
	{ path: pageurl.REQUESTLIST, actions: [ACTIONS.SYSTEM_MANAGEMENT_REQUEST_LIST, ACTIONS.SYSTEM_MANAGEMENT], origin: 'extra' },
	{ path: pageurl.AUDITTRAIL, actions: [ACTIONS.SYSTEM_MANAGEMENT_AUDIT_TRAIL, ACTIONS.SYSTEM_MANAGEMENT], origin: 'extra' },
	{
		path: pageurl.RESULTNOTIFICATIONS,
		actions: [ACTIONS.SYSTEM_MANAGEMENT_RESULT_NOTIFICATIONS, ACTIONS.SYSTEM_MANAGEMENT],
		origin: 'extra',
	},
	{
		path: pageurl.ELECTIONOFFICIALS,
		actions: [ACTIONS.SYSTEM_MANAGEMENT_ELECTION_OFFICIALS, ACTIONS.SYSTEM_MANAGEMENT],
		origin: 'extra',
	},

	/**
	 * MUST be explicit. `/admin/elections/analytics` prefix-resolves to `/admin/elections`
	 * → `election_view`, which a Candidate does NOT hold — so the legacy redirect would
	 * 403 for exactly the audience it exists to serve. This row is why the extras table
	 * was walked route by route instead of assumed.
	 */
	{
		path: pageurl.RESULTANALYTICS_LEGACY,
		actions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		origin: 'extra',
		note: 'Legacy redirect into Result Analytics — must not inherit Elections',
	},
	{ path: `${pageurl.RESULTANALYTICS_LEGACY}/*`, actions: [ACTIONS.ELECTION_RESULT_ANALYTICS], origin: 'extra' },

	/**
	 * `hide: true` means "not in the rail", NOT "no route". Leaving this to fall through
	 * to deny would mean flipping `hide` later silently 403s.
	 */
	{ path: pageurl.AI, actions: [ACTIONS.AI], origin: 'extra', note: 'Hidden from the rail but routable' },

	/**
	 * Help Centre. Reading is open to every signed-in user because the SERVER filters each guide
	 * by the module gates the reader holds — a route gate here would only duplicate that, badly.
	 * The editor is the one gated surface. `/admin/help/:slug` and `/admin/help/search` inherit the
	 * open row by prefix; `/admin/help/manage/:id` inherits the editor row, the longer prefix.
	 */
	/**
	 * The old Import Voters address, kept as a redirect into the Voters workspace. Same gate as the
	 * workspace, so a bookmark opens for exactly the people who could open the page it lands on.
	 */
	{
		path: pageurl.IMPORTVOTERS,
		actions: [ACTIONS.VOTERS_VIEW, ACTIONS.IMPORT_VOTERS],
		origin: 'extra',
		note: 'Legacy redirect into the Voters workspace',
	},

	{ path: pageurl.HELP, actions: null, origin: 'extra', note: 'Help Centre reader — server filters guides' },
	{ path: pageurl.HELP_MANAGE, actions: [ACTIONS.HELP_CENTRE_MANAGE], origin: 'extra', note: 'Help Centre editor' },
];

const fromMenu = (): IRoutePolicy[] => {
	const policies: IRoutePolicy[] = [];

	menuData.forEach((item) => {
		policies.push({
			path: item.pageUrl,
			actions: item.roleActions,
			superAdminOnly: item.superAdminOnly,
			requiredRoles: item.requiredRoles,
			excludedRoles: item.excludedRoles,
			origin: 'menu-item',
		});

		item.sub?.forEach((sub) => {
			const path = subItemPath(item, sub);

			// A `url: ''` sub shares the parent's path. Union rather than letting one
			// silently win — either grant should open the shared page.
			const existing = policies.find((policy) => policy.path === path);
			if (existing) {
				existing.actions = [...new Set([...(existing.actions ?? []), ...sub.roleActions])] as unknown as ActionSet;
				return;
			}

			policies.push({
				path,
				actions: sub.roleActions,
				superAdminOnly: sub.superAdminOnly,
				origin: 'menu-sub',
			});
		});
	});

	return policies;
};

/** Static segments dominate; a dynamic segment makes a pattern less specific. */
const specificity = (path: string): number => {
	const segments = path.split('/').filter(Boolean);
	const dynamic = segments.filter((segment) => segment.startsWith(':') || segment === '*').length;
	return (segments.length - dynamic) * 10 - dynamic;
};

export const ROUTE_POLICIES: readonly IRoutePolicy[] = [...fromMenu(), ...ROUTE_EXTRAS].sort(
	(a, b) => specificity(b.path) - specificity(a.path)
);

/**
 * Resolve the policy governing a pathname, or `null` when nothing claims it.
 *
 * `matchPath` matches on segment boundaries, which is the fix for `_isUrl`'s
 * `pathname.includes()`: `'users'` no longer matches both `/admin/management/users` and
 * `/admin/vesr/users`. Exact matches are preferred, then the longest prefix, so a
 * detail route inherits its module's policy without a hand-written row.
 */
export const resolveRoutePolicy = (pathname: string): IRoutePolicy | null => {
	const exact = ROUTE_POLICIES.find((policy) => matchPath({ path: policy.path, end: true }, pathname));
	if (exact) return exact;

	return ROUTE_POLICIES.find((policy) => matchPath({ path: policy.path, end: false }, pathname)) ?? null;
};

export interface IRouteVerdict {
	allowed: boolean;
	policy: IRoutePolicy | null;
	/** Codes that would have opened it — for the access-denied page. */
	missing: readonly string[];
}

/**
 * Decide whether the current principal may open a path.
 *
 * Fails OPEN while permissions are unresolved. An unresolved set almost always means
 * the session lapsed rather than access being revoked, and showing "no access" there
 * sends an operator to their administrator for a problem a re-login fixes. Once the
 * set IS resolved — including resolved-and-empty — this denies normally.
 */
export const evaluateRoute = (pathname: string, snapshot: IPermissionSnapshot): IRouteVerdict => {
	const policy = resolveRoutePolicy(pathname);

	if (!snapshot.isResolved) return { allowed: true, policy, missing: [] };

	// Nothing claims this path. Deny: an unlisted `/admin/*` route is either a typo or a
	// page nobody has classified, and guessing "open" is how a gate silently disappears.
	if (!policy) return { allowed: false, policy: null, missing: [] };

	if (policy.superAdminOnly && !isSuperAdmin(snapshot)) {
		return { allowed: false, policy, missing: policy.actions ?? [] };
	}

	if (policy.requiredRoles?.length && !hasAnyRole(snapshot, policy.requiredRoles)) {
		return { allowed: false, policy, missing: policy.actions ?? [] };
	}

	if (policy.excludedRoles?.length && hasAnyRole(snapshot, policy.excludedRoles)) {
		return { allowed: false, policy, missing: [] };
	}

	if (policy.actions === null) return { allowed: true, policy, missing: [] };

	const allowed = hasAny(snapshot, policy.actions);

	return { allowed, policy, missing: allowed ? [] : policy.actions };
};

export const isRouteAllowed = (pathname: string, snapshot: IPermissionSnapshot): boolean =>
	evaluateRoute(pathname, snapshot).allowed;

/**
 * The first page in menu order that this principal can both see in the menu and open.
 * Null when there is none.
 *
 * A team member's home follows their owner's (a Candidate's is My Election), but their grants
 * may not include that page. Landing them on a lock screen right after sign-in reads as "my
 * access is broken"; this finds the first page their grants do open instead.
 */
export const firstOpenablePage = (snapshot: IPermissionSnapshot, menu: readonly IMenuItem[] = menuData): string | null => {
	for (const item of menu) {
		if (!isMenuItemVisible(item, snapshot)) continue;
		const subs = item.sub?.length ? visibleSubItems(item, snapshot) : [];
		const candidates = subs.length ? subs.map((sub) => subItemPath(item, sub)) : [item.pageUrl];
		const open = candidates.find((path) => isRouteAllowed(path, snapshot));
		if (open) return open;
	}
	return null;
};
